import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { 
  Check, 
  ArrowLeft,
  ExternalLink,
  Download,
  PlayCircle,
  FileText,
  Ship,
  Clock,
  Users,
  Target,
  ChevronRight,
  Circle
} from 'lucide-react';

// Animation et background styles
const styles = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  .floating {
    animation: float 6s ease-in-out infinite;
  }

  .pulsing {
    animation: pulse 2s ease-in-out infinite;
  }
`;

function importAllProducts() {
  const context = require.context('./data/products', false, /\.json$/);
  const products = {};
  context.keys().forEach((key) => {
    // Extraire le nom du produit du chemin du fichier (sans .json)
    const productId = key.replace(/^\.\/(.*)\.json$/, '$1');
    products[productId] = context(key);
  });
  return products;
}
const productsData = importAllProducts();




// Composant Background
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
    <div className="absolute inset-0 opacity-30">
      <div className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '4rem 4rem',
          maskImage: 'radial-gradient(circle at 50% 50%, black, transparent 80%)'
        }}
      />
    </div>
  </div>
);

// Composant Breadcrumb
const Breadcrumb = ({ product }) => {
  const navigate = useNavigate();
  
  return (
    <div className="flex items-center gap-2 text-sm text-white/50 mb-8">
      <button
        onClick={() => navigate('/portfolio')}
        className="hover:text-white transition-colors flex items-center gap-1"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux solutions
      </button>
      <ChevronRight className="w-4 h-4" />
      <span className="text-white">{product.name}</span>
    </div>
  );
};

// Quick Actions Component
const QuickActions = ({ resources }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-black/0 
                  py-4 backdrop-blur-sm transform translate-y-full hover:translate-y-0 
                  transition-transform duration-300">
    <div className="container mx-auto px-4">
      <div className="flex justify-center gap-4">
        <a
          href={resources.demo}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all bg-[#017D7E] hover:bg-[#015F60] text-white`}
        >
          <PlayCircle className="w-5 h-5" />
          <span>Démo</span>
        </a>
        <a
          href={resources.documentation}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all bg-white/10 hover:bg-white/20 text-white/70 hover:text-white`}
        >
          <FileText className="w-5 h-5" />
          <span>Documentation</span>
        </a>
        <a
          href={resources.resources}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all bg-white/10 hover:bg-white/20 text-white/70 hover:text-white`}
        >
          <Download className="w-5 h-5" />
          <span>Ressources</span>
        </a>
      </div>
    </div>
  </div>
);
const ActionButton = ({ icon: Icon, label, href, primary }) => (
  <a
    href={href}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
      primary 
        ? 'bg-[#017D7E] hover:bg-[#015F60] text-white' 
        : 'bg-white/10 hover:bg-white/20 text-white/70 hover:text-white'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
  </a>
);
// Composants de section
const Section = ({ children, className = "" }) => (
  <section className={`py-12 md:py-20 ${className}`}>
    <div className="container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        {children}
      </div>
    </div>
  </section>
);

// Hero Section
const HeroSection = ({ product }) => (
  <Section className="pt-24 md:pt-32">
    <Breadcrumb product={product} />
    
    <div className="max-w-4xl mx-auto text-center mb-12">
      <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] 
                    mb-6 border border-green-500/20">
        {product.category}
      </div>
      
      <h1 className="text-4xl md:text-6xl font-bold mb-6">
        {product.name}
        <span className="block text-transparent bg-clip-text bg-gradient-to-r 
                      from-[#017D7E] to-[#00A99D]">
          {product.hero.title}
        </span>
      </h1>
      
      <p className="text-lg md:text-xl text-white/70 mb-8">
        {product.hero.description}
      </p>

      <div className="flex flex-wrap justify-center gap-4 mb-12">
        
      </div>
    </div>

    {/* Preview Image */}
    {product.hero.image && (
      <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <img 
          src={product.hero.image}
          alt={`${product.name} preview`}
          className="w-full object-cover"
        />
      </div>
    )}
  </Section>
);

// MetricCard Component
const MetricCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white/5 rounded-lg px-6 py-4 border border-white/10">
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-[#017D7E]" />
      <div>
        <div className="text-xl font-bold">{value}</div>
        <div className="text-sm text-white/50">{label}</div>
      </div>
    </div>
  </div>
);

// Features Section
const FeaturesSection = ({ features }) => (
  <Section className="bg-gradient-to-b from-transparent to-black/30">
    <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
      Fonctionnalités <span className="text-[#017D7E]">Clés</span>
    </h2>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {features.map((feature, index) => (
        <FeatureCard key={index} {...feature} />
      ))}
    </div>
  </Section>
);

// FeatureCard Component
const FeatureCard = ({ icon: Icon, title, description, features = [] }) => (
  <div className="group bg-white/5 rounded-xl p-6 border border-white/10 
                hover:border-green-500/50 transition-all">
    <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-4 
                  group-hover:scale-110 transition-transform">
      {Icon && <Icon className="w-6 h-6 text-[#017D7E]" />}
    </div>
    
    <h3 className="text-xl font-bold mb-3">{title || 'Sans titre'}</h3>
    <p className="text-white/70 mb-4">{description || ''}</p>
    
    {Array.isArray(features) && features.length > 0 && (
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0" />
            <span className="text-sm text-white/70">{feature}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Architecture Section
const ArchitectureSection = ({ architecture }) => (
  <Section>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      {/* Diagramme */}
      <div className="order-2 lg:order-1">
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          {architecture.diagram && (
            <img 
              src={architecture.diagram}
              alt="Architecture diagram"
              className="w-full rounded-lg"
            />
          )}
        </div>
      </div>

      {/* Description */}
      <div className="order-1 lg:order-2">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Architecture <span className="text-[#017D7E]">Technique</span>
        </h2>
        <p className="text-lg text-white/70 mb-8">{architecture.description}</p>
        
        <div className="space-y-6">
          {architecture.features.map((feature, index) => (
            <ArchFeature key={index} {...feature} />
          ))}
        </div>
      </div>
    </div>
  </Section>
);

// Architecture Feature Component
const ArchFeature = ({ icon, title, description, points }) => (
  <div className="bg-white/5 rounded-xl p-6 border border-white/10">
    <div className="flex items-start gap-4 mb-4">
      <div className="p-2 rounded-lg bg-[#017D7E]/10">
        <DynamicIcon 
          name={icon} 
          className="w-6 h-6 text-[#017D7E]" 
        />
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-white/70">{description}</p>
      </div>
    </div>
    <div className="pl-14">
      <div className="space-y-2">
        {points.map((point, index) => (
          <div key={index} className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-1 text-[#017D7E] flex-shrink-0" />
            <span className="text-sm text-white/70">{point}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Modules Section
const ModulesSection = ({ modules }) => {
  const [activeModule, setActiveModule] = useState(0);

  return (
    <Section className="bg-gradient-to-b from-transparent to-black/30">
      <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
        Modules <span className="text-[#017D7E]">Fonctionnels</span>
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Navigation */}
        <div className="lg:col-span-4">
          <div className="space-y-2">
            {modules.map((module, index) => (
              <button
                key={index}
                onClick={() => setActiveModule(index)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all
                         ${activeModule === index 
                           ? 'bg-[#017D7E] text-white' 
                           : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                         }`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    <DynamicIcon 
                      name={module.icon} 
                      className="w-5 h-5" 
                    />
                  </div>
                  <span>{module.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Module Details */}
        <div className="lg:col-span-8">
          <div className="bg-white/5 rounded-xl p-6 md:p-8 border border-white/10">
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-3">
                {modules[activeModule].name}
              </h3>
              <p className="text-white/70">
                {modules[activeModule].description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {modules[activeModule].features.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Check className="w-5 h-5 mt-1 text-[#017D7E] flex-shrink-0" />
                  <span className="text-white/70">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

// Dynamic Icon Component
const DynamicIcon = ({ name, ...props }) => {
  const IconComponent = Icons[name] || Icons.Circle;
  return <IconComponent {...props} />;
};
// Deployment Section
const DeploymentSection = ({ deployment, support }) => (
  <Section>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      {/* Options de Déploiement */}
      

    
    </div>
  </Section>
);

// CTA Section
const CTASection = ({ product, resources }) => (
  <Section className="bg-gradient-to-b from-transparent to-black/30">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-6">
        Prêt à Transformer Votre Business ?
      </h2>
      
      <p className="text-lg text-white/70 mb-8">
        Découvrez comment {product.name} peut révolutionner votre approche
      </p>

      <div className="flex flex-wrap justify-center gap-4">
        <a 
          href={resources.demo}
          className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] 
                    hover:from-green-600 hover:to-emerald-600 text-white font-medium 
                    transition-all flex items-center gap-2"
        >
          <PlayCircle className="w-5 h-5" />
          <span>Demander une démo</span>
        </a>

        <a 
          href={resources.documentation}
          className="px-8 py-4 rounded-lg border border-green-500/50 
                    hover:bg-[#017D7E]/10 text-white font-medium transition-all
                    flex items-center gap-2"
        >
          <FileText className="w-5 h-5" />
          <span>Plus de détail</span>
        </a>
      </div>
    </div>
  </Section>
);

// Composant Principal ProductPage
const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const product = productsData[productId];

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    // Simuler un chargement fluide
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => {
      styleSheet.remove();
      clearTimeout(timer);
    };
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Solution non trouvée</h2>
          <p className="text-white/70 mb-6">
            La solution demandée n'existe pas ou n'est pas accessible.
          </p>
          <button
            onClick={() => navigate('/portfolio')}
            className="px-6 py-3 rounded-lg bg-[#017D7E] text-white font-medium
                     hover:bg-[#015F60] transition-all"
          >
            Retour au catalogue
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4" />
          <p className="text-white/70">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <AnimatedBackground />
      
      <HeroSection product={product} />
      <FeaturesSection features={product.overview.keyPoints} />
      <ArchitectureSection architecture={product.architecture} />
      <ModulesSection modules={product.modules} />
      <DeploymentSection 
        deployment={product.deployment}
        support={product.support}
      />
      <CTASection 
        product={product}
        resources={product.resources}
      />
      <QuickActions resources={product.resources} />
    </div>
  );
};

export default ProductPage;